import Backbone from 'backbone'
import _ from 'lodash'
import $ from 'jquery'
import logPath from 'scoreboard/components/log-path'
import deviceType from 'scoreboard/components/device-type'
import ScrollListener from 'scoreboard/components/scroll-listener'
import autofocus from 'scoreboard/polyfills/autofocus'
import isSelectionInElement from 'scoreboard/components/is-selection-in-element'
import constants from 'scoreboard/components/constants'
import {isInDialog} from 'scoreboard/components/dialog'
import { isV5 } from 'scoreboard/components/version'

// different values for the "placement" property. You can write Tooltip.ABOVE, for example
const ABOVE = 'above'
const BELOW = 'below'

// options for the "arrow" property
const TOP = 'top'
const BOTTOM = 'bottom'
const CENTER = 'center'

// additional options for placement and arrow
const LEFT = 'left'
const RIGHT = 'right'

var Tooltip = Backbone.View.extend({
	className: 'tooltip',

	// Number of pixels the arrow is shifted inward on the tooltip.
	// Allows for more precise positioning of the tooltip on elements
	// not in the dom, such as icons rendered through :after CSS rules.
	// For example, if for position: 'bottom' and arrow: 'right', an
	// adjustment of 5 will move the arrow 5 pixels to the left of the
	// right edge of the element it's attached to.
	_adjustment: 0,

	_contentView: null,

	/**
	 * @constructs
	 *
	 * @param options
	 * @param {String} options.content text or html to display
	 * @param {String} [options.placement=Tooltip.BELOW] one of:
	 * Tooltip.ABOVE, Tooltip.BELOW, Tooltip.LEFT, Tooltip.RIGHT. see placement()
	 * @param {String} [options.arrow=Tooltip.RIGHT] one of:
	 * Tooltip.TOP, Tooltip.BOTTOM, Tooltip.CENTER, Tooltip.LEFT, Tooltip.RIGHT see arrow()
	 * @param {Boolean} [options.attachToCenter=true] indicates if arrow should point to the center of attachTo
	 * @param {Boolean} [options.ignoreAttachToClick=false] if true, event propagation will not be prevented
	 * @param {String} [options.alternatePlacements] Other placements to consider (in order) if the original placement fails
	 * @param {Boolean} [options.setTooltipBodyMaxHeight] If true, set a
	 * a max-height on the .tooltip-body to prevent content from overflowing
	 * the window. The tooltip will fill the available space between the
	 * element its attached to and the bottom or top of the window.
	 *
	 * @example
			new Tooltip({
				attachTo: this.$('.something'),
				attachToPoint: { x: 456, y: 789 }, // defaults to nothing, allows attaching arrow to a specific point
				attachToCenter: false, // defaults to true, indicates if arrow should point to the center of attachTo
				content: 'Hey, look at me! I\'m a tooltip!',
				placement: Tooltip.BELOW,
				alternatePlacements: [Tooltip.LEFT, Tooltip.RIGHT],
				arrow: Tooltip.RIGHT,
				adjustment: 5, // see _adjustment
				closeOnChangedFocus: false, // false if clicking outside the tooltips shouldn't close it (defaults to true)
				closeOnAttachedToClick: false, // false if clicking on attachedTo shouldn't close the tooltip (defaults to true),
				closeOnDialogOpen: false, // controls whether the tooltip should close if a dialog is open.  defaults to true.
					NOTE: This should NOT be used if the tooltip is already overlapping a dialog (.over-dialog). Tooltips can either
					overlap all dialogs, or be under all dialogs. They cannot appear in between dialogs - so tooltips which come from a dialog
					should close if they themselves launch another dialog.
				extraClass: 'has-error' // if you want a red error appearance, or to attach some other class
				left: 0 // If you'd like to override the left positioning of the tooltip, pass this in
			}).render() // IMPORTANT to call render!

		Valid placement/arrow combinations:
			- placement above/below can use arrow left, right, or center
			- placement left/right can use arrow top, bottom, or center
	 */
	initialize: function (options) {
		this.options = options
		this._initial = _.extend({placement: BELOW, arrow: RIGHT},
			_.pick(options, ['content', 'attachTo', 'attachToCenter', 'placement', 'arrow', 'adjustment', 'extraClass', 'ignoreAttachToClick', 'attachToPoint', LEFT, 'alternatePlacements']))

		if (validPlacementArrowCombos.hasOwnProperty(this._initial.placement)) {
			var validArrows = validPlacementArrowCombos[this._initial.placement]
			if (!_.includes(validArrows, this._initial.arrow)) {
				throw new Error('arrow "' + this._initial.arrow + '" is not valid for placement "' + this._initial.placement
					+ '"; valid arrow options are: ' + validArrows.join(', '))
			}
		} else {
			throw new Error('Invalid placement: "' + this._initial.placement + '"')
		}

		this.closeOnChangedFocus = _.isBoolean(options.closeOnChangedFocus) ? options.closeOnChangedFocus : true
		this.closeOnAttachedToClick = _.isBoolean(options.closeOnAttachedToClick) ? options.closeOnAttachedToClick : true
		_.bindAll(this, '_onDocumentClick', 'remove', '_onAttachedToClick', 'reposition', '_onResize')

		this._scrollListener = new ScrollListener()
		this.closeOnDialogOpen = _.isNil(options.closeOnDialogOpen) || options.closeOnDialogOpen

		this._attachListeners()
		this.rendered = false
	},

	_attachListeners() {
		// Here's when we want to close out tooltips:
		// - A dialog was opened and this.closeOnDialogOpen == true
		// - We've changed routes in the app
		// - We've changed orgs (#38500)
		if (this.closeOnDialogOpen) {
			this.listenTo(Backbone, 'dialog:open', () => this.remove())
		}
		this.listenTo(Backbone.history, 'route', () => this.remove())
		this.listenTo(Backbone, 'change:org', () => this.remove())
	},

	render: function () {
		if (!this._initial.content) {
			throw new Error('content is required')
		}

		if (this._initial.content.render) {
			logPath('tooltip', this._initial.content)
			this._contentView = this._initial.content
			this._contentView.on('done', this.remove)
			this.$el.html(this._contentView.el)
			this._contentView.render()
		} else {
			this.$el.html(this._initial.content)
		}
		this._attachedTo = $(this._initial.attachTo)

		// Detect when tooltip in dialog and auto add class to make it show on top
		if (isInDialog(this._attachedTo)) {
			let extraClass = this._initial.extraClass || ''
			extraClass += ' over-dialog'
			this._initial.extraClass =  extraClass
		}
		this._initial.extraClass && this.$el.addClass(this._initial.extraClass)

		this._attachToCenter = !(this._initial.attachToCenter === false)
		this._attachToPoint = this._initial.attachToPoint
		this._adjustment = this._initial.adjustment

		// Need to add it to the tooltip-container so we get accurate dimensions;
		// it won't be visible until 'open' class is added
		$('.tooltip-container').append(this.el)

		// Now we can position it
		this.position(this._initial)
		delete this._initial

		// Now that it's position, the 'open' class will make it visible
		this.$el.addClass('open')

		// Delay adding the "close listeners" (eg close on click outside the tooltip)
		// so that the click event that opened the tooltip won't immediately close it
		process.nextTick(_.bind(this._addCloseListeners.bind(this)))

		// When the contents change, we may need to reposition the tooltip
		this.resizeObserver = new MutationObserver(this.reposition)
		this.resizeObserver.observe(this.el, observerOptions)

		// If a content change uses transitions, the MutationObserver may reposition based
		// on old dimensions. We need to make sure we reposition when the transition is done
		this.$el.on('transitionend', this.reposition)

		// If tooltp is present and user scrolls on mobile, adjust the positioning of the tooltip
		// so it stays attached to the correct element.
		if (deviceType.touch) {
			var parent = $(this._attachedTo)
			// The scrollable parent should either be the display-layout or a dialog-body
			this.tooltipScrollContainer = $('.dialog-body, .display-layout').has(parent)
			this.tooltipScrollContainer.on('scroll', this.reposition)
		}

		autofocus(this.el)

		if (this._attachedTo[0]) {
			let rect = nodeCoords(this._attachedTo[0])
			this._initialOffsetCoords = _.pick(rect.offset, TOP, LEFT)
		}

		// If the tooltip is opened in a dialog, keep track of the dialog
		// so we can close the tooltip when necessary
		if (!this.closeOnDialogOpen) {
			this.currentDialog = $('.dialog').last()
		}

		this.rendered = true

		return this
	},

	// get or set the content of the tooltip
	content: function(c) {
		var returnVal = this.$el.html(c)
		if (c) {
			this.reposition()
		}
		return returnVal
	},

	reposition: function () {
		// If the element the tooltip was attached to is no longer visible, then
		// we won't be able to reposition. Just leave the tooltip where it is
		if (this.attachedTo() && !this.attachedTo().is(':visible')) {
			return
		}

		// Temporarily disconnect the mutation observer or we'll go into an infinite loop when we reposition it
		this.resizeObserver.disconnect()

		this.position({
			arrow: this.arrow(),
			placement: this.placement(),
			adjustment: this._adjustment
		})

		this.resizeObserver.observe(this.el, observerOptions)
	},

	toggle(show) {
		this.$el.toggleClass('is-not-displayed', !show)
	},

	hide() {
		this.toggle(false)
	},

	show() {
		this.toggle(true)
	},

	// If no parameters, returns the placement of the tooltip relative to the element it's
	// attached to: above, below, left, right.
	// If a parameter, will set the placement to that value, though position() will need to
	// be called to actually move it
	placement: function (p) {
		if (!p) {
			return getPrefixedClass(this.$el, 'placement')
		}

		var current = this.placement()
		if (current == p) {
			return
		}

		this.$el.removeClass('placement-' + current).addClass('placement-' + p)
	},

	// If no parameters, returns the location of the tooltip's arrow on the side facing
	// the element it's attached to: left, right, top, bottom, center
	// If a parameter, will set the arrow position to that value, though position() will need to
	// be called to move the tooltip appropriately
	arrow: function (a) {
		if (!a) {
			return getPrefixedClass(this.$el, 'arrow')
		}

		var current = this.arrow()
		if (current == a) {
			return
		}

		this.$el.removeClass('arrow-' + current).addClass('arrow-' + a)
	},

	attachedTo: function (newAttachedTo = null) {
		if (!newAttachedTo) return this._attachedTo
		this._attachedTo = newAttachedTo
		this.reposition()
	},

	attachToCenter: function () {
		return this._attachToCenter
	},

	// Positions the tooltip using the provide options for placement, arrow, and adjustment
	// Actual position may be different if the provided options won't fit in the window
	position: function (opt) {
		if (!(this._attachedTo && this._attachedTo.length > 0)) {
			throw new Error('attachTo is required')
		}

		let fitResults = fit(this.$el, this._attachedTo,
			{
				height: document.documentElement.clientHeight,
				width: document.documentElement.clientWidth,
				left: opt.left
			},
			_.extend({}, opt, {
				placement: opt.placement || this.placement(),
				arrow: opt.arrow || this.arrow(),
				adjustment: opt.adjustment || this._adjustment || 0,
				attachToCenter: this.attachToCenter(),
				attachToPoint: this._attachToPoint
			}))
		let placement = fitResults.placement
		this.placement(placement)
		this.arrow(fitResults.arrow)

		if (this.options.setTooltipBodyMaxHeight &&
			[ABOVE, BELOW].includes(placement)) {
			this.setTooltipBodyMaxHeight(placement)
		}
		// Issue #9482: Whenever the keyboard is open on iOS, the bottom/top positioning is no longer accurate.
		// Instead, the value takes its real value plus or minus the distance the document has been
		// scrolled from the top depending on if we are getting the top or bottom value.
		var scrollTop = deviceType.safari && deviceType.mobileIOSAndriod ? $(document).scrollTop() : 0

		var position = {}
		if (fitResults.top === 'inherit') {
			// If iOS keyboard is open, the bottom value takes its real value plus the distance the document has been
			// scrolled from the top. Therefore, we have to subtract that amount. Amount will be 0 if not in iOS
			position.top = 'inherit'
			position.bottom = fitResults.bottom - scrollTop + 'px'
		} else {
			// Top position has the same issue in iOS as the comment above for bottom positiong. Except it subtracts
			// the scrollTop value instead. So we need to add it back on.
			position.top = fitResults.top + scrollTop + 'px'
			position.bottom = 'inherit'
		}

		if (fitResults.left === 'inherit') {
			position.left = 'inherit'
			position.right = fitResults.right + 'px'
		} else {
			position.left = fitResults.left + 'px'
			position.right = 'inherit'
		}

		this.$el.css(position)
	},

	/**
	 * Calculates the maximum height for a tooltip that is placed above or
	 * below the attachedTo element
	 */
	setTooltipBodyMaxHeight: function(placement) {
		let attachedTo = this._attachedTo ? $(this._attachedTo) : null
		let attachedToOffset = attachedTo.offset()
		let windowScrollTop = $(window).scrollTop()
		let windowInnerHeight = window.innerHeight
		let availableSpace

		if (placement === ABOVE) {
			availableSpace = attachedToOffset.top - windowScrollTop
		} else if (placement === BELOW) {
			let attachedToBottom =
				attachedToOffset.top + attachedTo.outerHeight()
			availableSpace =
				windowInnerHeight + windowScrollTop - attachedToBottom
		} else {
			return
		}

		// Sets a max height on the tooltip content when the tooltip is
		// above or below the attachedTo element. Add the extra margin to
		// keep it from touching the edge of the window which removes
		// the arrow.
		let maxTooltipHeight = availableSpace - constants.STANDARD_MARGIN*3

		// Set the max height for the tooltip content
		this.$('.tooltip-body').css('max-height', maxTooltipHeight + 'px')
	},

	_addCloseListeners: function () {
		$(document).on('click', this._onDocumentClick)
		$(document).on('touchstart', this._onDocumentClick)
		$(window).on('resize', this._onResize)
		$(this._attachedTo).on('click', this._onAttachedToClick)
		if (isV5()) {
			// When the navPane expands, blow away any tooltips in the display-pane/slide-pane lest
			// they overlap that navPane
			Backbone.on('navPane:expanded', () => {
				if (this._attachedTo.parents('.display-pane').length > 0 ||
						this._attachedTo.parents('.slide-pane').length > 0) {
					this.remove()
				}
			})
		}

		// close tooltip on scroll, except for touch, because touching the
		// document will close the tooltip for us
		if (!deviceType.touch) {
			this._scrollListener.on(this._attachedTo, () => {
				// #19441 Safari sometimes fires scroll events even though no scroll
				// appeared to happen. To guard against this, only close the tooltip
				// if the coordinates of the attachTo element changed (relative to
				// the viewport)
				if (this.attachedToMoved()) {
					if (_.result(this.options, 'keepOpenOnScroll')) {
						// If the tooltip should be kept open, just reposition it
						this.reposition()
					} else {
						this.remove()
					}
				}
			})
		}
	},

	_onResize: function() {
		// If we're on a touch iOS device and we recieve a document resize event,
		// that likely means the keyboard or bottom autocomplete bar is coming into
		// view, or possibly, the orientation is changing. In those cases, let the
		// tooltip stick around (#26828)
		if (deviceType.safari && deviceType.mobileIOSAndriod) {
			// Give some extra time before repositioning the tooltip to account for the
			// iOS orientation change animation.
			process.nextTick(() => this.reposition())
			return
		}

		this.remove()
	},

	/**
	 * Returns true if the "attachedTo" node is in a different position (relative
	 * to the viewport) than when the tooltip was created.
	 */
	attachedToMoved () {
		if (!this._attachedTo[0]) {
			return false
		}

		let currentCoords = nodeCoords(this._attachedTo[0]).offset
		return this._initialOffsetCoords.top != currentCoords.top
			|| this._initialOffsetCoords.left != currentCoords.left
	},

	_onAttachedToClick: function (e) {
		if (!this.closeOnAttachedToClick) {
			return
		}

		this.remove()
		// the node to which the tooltip is attached probably has a click
		// handler to open it, so stop propagation to prevent it from
		// re-opening right away
		if(!this.options.ignoreAttachToClick) {
			e.stopPropagation()
		}
	},

	_removeCloseListeners: function () {
		$(document).off('click', this._onDocumentClick)
		$(document).off('touchstart', this._onDocumentClick)
		$(window).off('resize', this._onResize)
		$(this._attachedTo).off('click', this._onAttachedToClick)

		this._scrollListener.off()
	},

	_onDocumentClick: function (e) {
		if (!this.closeOnChangedFocus) {
			return
		}
		if (isSelectionInElement(this.el)) { // #24896
			// Don't close if user is selecting text and mouseups outside the input
			return
		}

		if (this.$el.is(e.target) || this.$el.has(e.target).length > 0
				|| $(this._attachedTo).is(e.target) || $(this._attachedTo).has(e.target) > 0 ||
				// #26865 user may have clicked on something that is no longer in
				// the DOM (like selecting a date in the next month in the date picker).
				// We don't really know if that means they were clicking on something
				// inside the tooltip, but it seems like a fair guess
				!inDOM(e.target)) {
			return
		}
		// if we're staying open when a dialog opens, we should keep it open if
		// a click happens within the dialog that was opened after the tooltip
		// was created. If a click happens within the dialog in which the tooltip
		// was opened, we should close the tooltip.
		let closestDialog = $(e.target).closest('.dialog')
		let isNewDialog = !this.currentDialog?.length ||
			closestDialog[0] !== this.currentDialog[0]
		if (!this.closeOnDialogOpen && closestDialog.length && isNewDialog) {
			return
		}

		// ignore if the event was generated in another tooltip (that tooltip
		// should handle what to do with the event, and propagate whatever behavior
		// should be done to allow multi-level tooltips)
		// Select menu's also act like tooltips, so check to make sure the target
		// wasn't a select menu. This allows things like touch events selecting
		// select options without closing the tooltip. #51930
		let eventInTooltip = $(e.target).closest('.tooltip, .select-menu')
		if (eventInTooltip.length > 0 && !eventInTooltip.is(this.$el)) {
			return
		}
		this.remove()
	},

	remove: function () {
		// #32218 don't remove over and over (avoids Maximum call stack error)
		if (this.removed) return
		// don't allow closeOnDialogOpen behavior to make tooltips linger if
		// the tooltip was part of a dialog, but was created and not rendered
		// before the dialog launched. This problem was evident for
		// this.notEditableTooltip in record-list-widget-editor.js
		if (!this.rendered) return

		this.removed = true

		this.tooltipScrollContainer && this.tooltipScrollContainer.off('scroll', this.reposition)

		Backbone.View.prototype.remove.apply(this, arguments)

		this._removeCloseListeners()

		this._contentView && this._contentView.remove()
		this.resizeObserver && this.resizeObserver.disconnect()
	}
}, { ABOVE, BELOW, TOP, BOTTOM, CENTER, LEFT, RIGHT })

// Returns the non-prefixed version of the current class on the given element that has the given prefix.
// eg if $el has the class 'arrow-left', then getPrefixedClass($el, 'arrow') returns LEFT
function getPrefixedClass ($el, prefix) {
	var classes = $el.attr('class').split(' ')
	var prefixedClass = _.find(classes, function (c) { return c.indexOf(prefix + '-') == 0})
	return !!prefixedClass ? prefixedClass.replace(prefix + '-', '') : null
}

// Given placement and arrow, return the resulting coords and whether there is still overflow
// with those coords.
function getOverflow(coordOptions) {
	let testCoords = coords(coordOptions)
	let maxOverflow, placementOverflow = overflow(testCoords, coordOptions.container)
	for (const o in placementOverflow) {
		if (_.isNil(maxOverflow) || maxOverflow.size < placementOverflow[o]) {
			maxOverflow = {
				size: placementOverflow[o],
				side: o
			}
		}
	}
	return {
		coords: testCoords,
		overflow: maxOverflow
	}
}

// Given placement options, return the resulting coordinates/arrow that'll make that placement work
// for the tooltip. Nothing is returnd if the placement will not work in making the tooltip fit.
function testPlacement(options) {
	let result,
		arrow = options.initialArrow,
		starting = getOverflow(_.extend({ arrow }, options))
	if (starting.overflow.size == 0) {
		// No overflow, go ahead and use the current settings/coordinates.
		result = starting.coords
	} else {
		// There's overflow, try centering the arrow to see if that gets rid of it.
		arrow = CENTER
		let centered = getOverflow(_.extend({ arrow }, options))
		if (centered.overflow.size == 0) {
			result = centered.coords
		} else {
			// Centering didn't work, see if the overflow is perpendicular (eg we're trying to put the tooltip right,
			// and its overflow on the bottom). The best we can do to fix this is to
			// shift the arrow to the side of the overflow, so most of the content goes
			// away from the overflow (eg tooltip is on the right, overflow is on the
			// bottom, so put the arrow on the bottom so most of the content goes upwards)
			let perpSides = placementPerpendiculars[options.placement]
			for (let side of perpSides) {
				if (sideToPlacement[starting.overflow.side] == side) {
					let perpendicular = getOverflow(_.extend({ arrow: starting.overflow.side }, options))
					if (perpendicular.overflow.size == 0) {
						arrow = starting.overflow.side
						result = perpendicular.coords
					}
				}
			}
		}
	}

	// If we have a resulting placement that actually makes the tooltip fit, go ahead and return it
	if (result) {
		// issue #5466 shows a problem where a tooltip attached to a marker on the
		// right of the screen has an arrow which points to the left of the marker.
		// If the marker is too small we should change the placement of the arrow
		// to be on the extreme right of the tooltip so that it lines up with the
		// marker better.
		if(arrow == RIGHT && options.attachedTo.outerWidth() < 40) {
			arrow = 'far-right'
			result.arrow = arrow
		}

		return _.extend({ placement: options.placement, arrow }, result)
	}
}

// Detaches the tooltip from the attachTo element and returns the coordinates it should be using to best
// fit within the contianer
function detatch(coordOptions) {
	let initialCoords = coords(coordOptions)

	let currentOverflow = overflow(initialCoords, coordOptions.container)
	let detachedCoords = _.cloneDeep(initialCoords)

	if (currentOverflow.top) {
		// if tooltip is overflowing at the top, line it up with the
		// top of the screen.
		//
		// #20291 note that it could be overflowing both top and bottom;
		// in that case, it's better to see the top of the tooltip and
		// cut off the bottom
		detachedCoords.top = 0
		detachedCoords.bottom = 'inherit'
		detachedCoords.detached = true
	} else if (currentOverflow.bottom) {
		// if tooltip is overflowing at the bottom, line it up with the
		// bottom of the screen
		detachedCoords.bottom = 0
		detachedCoords.top = 'inherit'
		detachedCoords.detached = true
	}

	if (currentOverflow.left) {
		// if tooltip is overflowing on the left, line it up with the
		// left of the screen.
		//
		// #20291 note that it could be overflowing both left and right;
		// in that case, it's better to see the left of the tooltip and
		// cut off the right
		detachedCoords.left = 0
		detachedCoords.right = 'inherit'
		detachedCoords.detached = true
	} else if (currentOverflow.right) {
		// if tooltip is overflowing on the right, line it up with the
		// right of the screen
		detachedCoords.right = 0
		detachedCoords.left = 'inherit'
		detachedCoords.detached = true
	}

	return _.extend({placement: coordOptions.placement, arrow: 'none'}, detachedCoords)
}

/**
 * Fit finds the best location/settings for the given tooltip. It attempts to use the provide options, but will
 * alter the tooltip if it doesn't fit in the given container (usually 'window').
 *
 * By default, the only alternative placement considered is the oppsite side of whatever the initial placement was.
 * If alternatePlacements has been passed into the tooltip as an option, those placements are instead considered as alternatives.
 * For each placement, arrow shifting is done to see if that'll make the tooltip fit. Ultimately, if no potision/arrow
 * combination is found that makes the tooltip fit in the container, the tooltip is then detached from the attachedTo element
 * and positioned as best as possible.
 *
 * Making this a static function on module so we can test it.
 *
 * @param {JQuery} tooltip The tooltip element
 * @param {JQuery} attachedTo The element the tooltip is to be attchedTo
 * @param {JQuery} container The container the tooltip should stay within
 * @param {Object} opt Position-related options to be considered when calculating coordinates
 *
 */
var fit = Tooltip.fit = function (tooltip, attachedTo, container, opt) {
	let initialPlacement = opt.placement
	let initialArrow = opt.arrow
	let fittingPlacement, placements = _.isNil(opt.alternatePlacements) ? [initialPlacement, placementOpposites[initialPlacement]]
		: [initialPlacement, ...opt.alternatePlacements]

	// Go through and try all the possible placements until we hit one that fits
	for (let p of placements) {
		let tested = testPlacement({
			placement: p,
			initialArrow,
			adjustment: opt.adjustment,
			tooltip,
			attachedTo,
			attachToPoint: opt.attachToPoint,
			attachToCenter: true,
			container
		})
		if (!_.isNil(tested)) {
			fittingPlacement = tested
			break
		}
	}

	// If there was a placement that fit, return it's coords
	if (!_.isNil(fittingPlacement)) {
		return fittingPlacement
	}

	// At this point, no placement or arrow shifting made the tooltip fit, so detach the tooltip from its attachedTo element,
	// and shift it onto the screen as best we can. #7271
	return detatch({
		placement: initialPlacement,
		arrow: initialArrow,
		adjustment: opt.adjustment,
		tooltip,
		attachedTo,
		attachToPoint: opt.attachToPoint,
		attachToCnter: opt.attachToCenter,
		container
	})
}

// Maps an overflow side to the equivalent 'placement' option
var sideToPlacement = {}
sideToPlacement[TOP] = ABOVE
sideToPlacement[BOTTOM] = BELOW
sideToPlacement[LEFT] = LEFT
sideToPlacement[RIGHT] = RIGHT

var placementOpposites = {}
placementOpposites[ABOVE] = BELOW
placementOpposites[BELOW] = ABOVE
placementOpposites[RIGHT] = LEFT
placementOpposites[LEFT] = RIGHT

var placementPerpendiculars = {}
placementPerpendiculars[ABOVE] = [LEFT, RIGHT]
placementPerpendiculars[BELOW] = [LEFT, RIGHT]
placementPerpendiculars[RIGHT] = [ABOVE, BELOW]
placementPerpendiculars[LEFT] = [ABOVE, BELOW]

// Returns the amount of overflow to each side that the given coordinates would cause
// in the given container. 0 indicates no overflow for that side
function overflow (coords, container) {
	var over = {}

	if (coords.left === 'inherit') {
		over.left = Math.max(0, coords.right + coords.width - container.width)
		over.right = Math.max(0, -coords.right)
	} else {
		over.left = Math.max(0, -coords.left)
		over.right = Math.max(0, coords.left + coords.width - container.width)
	}

	if (coords.top === 'inherit') {
		over.top = Math.max(0, coords.bottom + coords.height - container.height)
		over.bottom = Math.max(0, -coords.bottom)
	} else {
		over.top = Math.max(0, -coords.top)
		over.bottom = Math.max(0, coords.top + coords.height - container.height)
	}

	return over
}

function nodeCoords (node) {
	node = $(node)

	if (node[0].getBoundingClientRect) {
		let rect = node[0].getBoundingClientRect()
		return {
			offset: rect,
			outerHeight: node.outerHeight(),
			outerWidth: node.outerWidth()
		}
	} else {
		// handle text nodes (e.g. for the equation editor)
		var range = document.createRange()
		range.selectNodeContents(node[0])

		// This doesn't put the tooltip in the best possible place
		// but at least it doesn't blowup
		let rect = range.getClientRects()[0]
		return {
			offset: rect,
			outerHeight: rect.height,
			outerWidth: rect.width
		}
	}
}

// For the given tooltip options, returns the appropriate coordinates (top, left, height, and width)
function coords (opt) {
	var attachedTo = $(opt.attachedTo)
	let { offset: attachedToOffset, outerHeight, outerWidth } = nodeCoords(attachedTo)

	// By default, use the location/dimensions of the DOM node to determine where
	// the arrow should be positioned
	let attachToCoords = _.extend(_.pick(attachedToOffset, TOP, LEFT, 'width', 'height'), {
		outerHeight,
		outerWidth
	})

	if (opt.attachToPoint) {
		// if an explicit attachment point is provided, override the attachToCoords

		if (opt.attachToPoint.x) {
			attachToCoords.left = opt.attachToPoint.x
			attachToCoords.width = attachToCoords.outerWidth = 0
		}

		if (opt.attachToPoint.y) {
			attachToCoords.top = opt.attachToPoint.y
			attachToCoords.height = attachToCoords.outerHeight = 0
		}
	}

	var tooltip = opt.tooltip
	var tooltipWidth = tooltip.outerWidth()
	var tooltipHeight = tooltip.outerHeight()
	let adjustment = opt.adjustment || 0

	var left, right, top, bottom
	left = right = top = bottom = 'inherit'

	if (opt.placement == BELOW || opt.placement == ABOVE) {
		if (opt.placement == BELOW) {
			top = attachToCoords.height + attachToCoords.top + arrowHeight
		} else {
			var y = attachToCoords.top - tooltipHeight - arrowHeight
			bottom = opt.container.height - y - tooltipHeight
		}

		var attachToAdjustment = opt.attachToCenter ? attachToCoords.width / 2 : attachToCoords.width

		if (opt.arrow == LEFT) {
			left = attachToCoords.left - arrowSpacingFromEdge - arrowHalfWidth + attachToAdjustment + adjustment
		} else if (opt.arrow == RIGHT || opt.arrow == 'far-right') {
			var x = attachToCoords.left + attachToAdjustment - tooltipWidth + arrowSpacingFromEdge + arrowHalfWidth - adjustment

			if (opt.arrow == 'far-right') {
				// Need to shift x further to account for the "far-right" position created for #5466
				x -= 16
			}

			right = opt.container.width - x - tooltipWidth
		} else {
			left = attachToCoords.left + attachToAdjustment - (tooltipWidth / 2) + adjustment
		}
	} else if (opt.placement == RIGHT || opt.placement == LEFT) {
		if (opt.placement == RIGHT) {
			left = attachToCoords.outerWidth + attachToCoords.left + arrowHeight
		} else {
			var x = attachToCoords.left - tooltipWidth - arrowHeight
			right = opt.container.width - x - tooltipWidth
		}

		var attachToAdjustment = opt.attachToCenter ? attachToCoords.outerHeight / 2 : attachToCoords.outerHeight

		if (opt.arrow == TOP) {
			top = attachToCoords.top + attachToAdjustment - arrowSpacingFromEdge - arrowHalfWidth + adjustment
		} else if (opt.arrow == BOTTOM) {
			var y = attachToCoords.top + attachToAdjustment - tooltipHeight + arrowSpacingFromEdge + arrowHalfWidth - adjustment
			bottom = opt.container.height - y - tooltipHeight
		} else {
			top = attachToCoords.top + attachToAdjustment - (tooltipHeight / 2) + adjustment
		}
	}

	return {
		left: _.isNil(opt.container.left) ? left : opt.container.left - tooltipWidth/2,
		right: right,
		top: top,
		bottom: bottom,
		width: tooltipWidth,
		height: tooltipHeight
	}
}

var validPlacementArrowCombos = {}
validPlacementArrowCombos[ABOVE] = [ LEFT, CENTER, RIGHT ]
validPlacementArrowCombos[LEFT] = [ TOP, CENTER, BOTTOM ]

validPlacementArrowCombos.below = _.clone(validPlacementArrowCombos.above)
validPlacementArrowCombos.right = _.clone(validPlacementArrowCombos.left)

// These values come from tooltip.less

// the @outer-border-width value from tooltip.less
var arrowHeight = constants.TOOLTIP_ARROW_HEIGHT
var arrowHalfWidth = constants.TOOLTIP_ARROW_WIDTH

// the @arrow-width value from tooltip.less
var arrowSpacingFromEdge = constants.TOOLTIP_ARROW_SPACING_FROM_EDGE

var observerOptions = {
	childList: true,
	subtree: true,
	attributes: true
}

/**
 * Returns true if the given element is inside the DOM tree
 */
function inDOM (el) {
	return $(document).has(el).length > 0
}

export default Tooltip

Tooltip.__filename__ = 'components/tooltip.js'
